import { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  pointerWithin,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragStartEvent,
  type DragOverEvent,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import type { PlaylistAsset, Asset, Playlist } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { isAssetExcludedFromPicker, ASSET_PICKER_TYPES } from '@scio3/shared-types';
import { DEFAULT_DURATION, PLAYLIST_THUMBNAIL_URL } from '../utils/playlist-constants';
import { enforceMinDuration } from '../utils/duration-helpers';
import { useFolderAdd } from '../hooks/useFolderAdd';
import { validateAssetAdd, validateMultipleNestedPlaylistAdd } from '../utils/playlist-validation';

/** Lightweight fragment for reading cached assets (no network request). */
const ASSET_CACHE_FRAGMENT = gql`
  fragment AssetCacheRead on Asset {
    _id
    type
    fileType
    appType
    webType
    commonType
    subType
    serviceType
    originalFileName
    thumbnail
    duration
    num_pages
    bucket
    AWSS3ID
  }
`;

/** Lightweight fragment for reading cached playlists (no network request). */
const PLAYLIST_CACHE_FRAGMENT = gql`
  fragment PlaylistCacheRead on Playlist {
    _id
    name
    totalDuration
  }
`;

// ── Drag data discriminated union ──

export type DragItemData =
  | {
      source: 'editor';
      index: number;
      item: PlaylistAsset;
      selectedCount: number;
    }
  | {
      source: 'asset-picker';
      assetIds: string[];
      name: string;
      thumbnail?: string;
      count: number;
    }
  | {
      source: 'playlist-list';
      playlistIds: string[];
      name: string;
      color?: string;
      count: number;
    }
  | {
      source: 'asset-folder';
      folderId: string;
      folderPath: string;
      name: string;
      thumbnail?: string;
    };

// ── Editor callbacks registered by PlaylistEditorPanel ──

export interface EditorDndCallbacks {
  activePlaylistId: string;
  localItems: PlaylistAsset[];
  /** The current playlist object (for duration limit validation). */
  playlist: Playlist | null;
  /** Whether the user can edit this playlist (folder permission check). */
  canEditPlaylist: boolean;
  handleReorder: (activeId: string, overId: string) => void;
  /** Optimistic add — inserts items into local state and marks dirty for debounced auto-save. */
  handleAddItems: (optimisticItems: PlaylistAsset[], pos: number) => void;
  validateNested: (args: {
    currentPlaylistId: string;
    ids: string[];
  }) => Promise<{ status: boolean; key?: string; value?: Record<string, unknown> }>;
  cancelAutoSave: () => void;
  resumeAutoSave: () => void;
}

// ── Context ──

interface PlaylistDndContextValue {
  /** Called by PlaylistEditorPanel to register/unregister its drag-related callbacks. */
  registerEditor: (callbacks: EditorDndCallbacks | null) => void;
  /** Index where external drop indicator should display (null = hidden). */
  externalDropIndex: number | null;
  /** True when an external drag (asset/playlist) is over the editor area. */
  isExternalDragOverEditor: boolean;
  /** Data about the currently dragged item (null when not dragging). */
  activeDragData: DragItemData | null;
  /** Cancel auto-save before opening dialogs that interrupt the save flow. */
  cancelAutoSave: () => void;
  /** Check duration limit before adding items via the picker (non-DnD path). Returns true if allowed. */
  checkDurationLimit: (itemsToAdd: PlaylistAsset[]) => boolean;
  /** Whether the user can edit the active playlist in the center editor (folder permission check). */
  canEditActivePlaylist: boolean;
  /** Unified: add assets by ID (validates + optimistic local add + debounced save). */
  addAssetsToEditor: (assetIds: string[]) => void;
  /** Unified: add folder assets (validates + optimistic local add + debounced save). */
  addFolderAssetsToEditor: (edges: Array<{ node: Asset }>) => void;
  /** Unified: add nested playlists (validates nesting + duration + optimistic local add). */
  addPlaylistsToEditor: (playlistIds: string[]) => Promise<void>;
}

const PlaylistDndCtx = createContext<PlaylistDndContextValue>({
  registerEditor: () => {},
  externalDropIndex: null,
  isExternalDragOverEditor: false,
  activeDragData: null,
  cancelAutoSave: () => {},
  checkDurationLimit: () => true,
  canEditActivePlaylist: true,
  addAssetsToEditor: () => {},
  addFolderAssetsToEditor: () => {},
  addPlaylistsToEditor: async () => {},
});

export const usePlaylistDnd = () => useContext(PlaylistDndCtx);

// ── Custom collision detection ──
// Editor-source drags (sortable reorder): use closestCenter for smooth item swapping.
// External drags (asset-picker / playlist-list): use pointerWithin to detect if the
// pointer is inside the editor drop zone, then closestCenter among sortable items
// for precise insertion position.

function createPlaylistCollisionDetection(
  activeDragRef: React.RefObject<DragItemData | null>,
): CollisionDetection {
  return (args) => {
    const data = activeDragRef.current;

    // Editor-internal reorder: only allow drops while pointer is inside the editor zone.
    if (data?.source === 'editor') {
      const insideEditor = pointerWithin({
        ...args,
        droppableContainers: args.droppableContainers.filter(({ id }) => id === 'editor-drop-zone'),
      });
      if (insideEditor.length === 0) return [];
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter(({ id }) => id !== 'editor-drop-zone'),
      });
    }

    // External drag: first check if pointer is within the editor-drop-zone
    const editorZoneCollisions = pointerWithin({
      ...args,
      droppableContainers: args.droppableContainers.filter(({ id }) => id === 'editor-drop-zone'),
    });

    if (editorZoneCollisions.length > 0) {
      const pointerY = args.pointerCoordinates?.y;
      if (!pointerY) return editorZoneCollisions;

      // Find the correct insertion position by comparing pointer Y to each
      // visible item's midpoint. Items are sorted by their numeric index so
      // we walk top-to-bottom: the first item whose midpoint is below the
      // pointer is the insertion target ("insert before this item").
      const sortable = args.droppableContainers
        .filter(({ id }) => id !== 'editor-drop-zone')
        .sort((a, b) => Number(a.id) - Number(b.id));

      for (const container of sortable) {
        const rect = args.droppableRects.get(container.id);
        if (!rect) continue;
        if (pointerY < rect.top + rect.height / 2) {
          return [{ id: container.id, data: { droppableContainer: container, value: 0 } }];
        }
      }

      // Pointer is below all visible items → drop at end
      return editorZoneCollisions;
    }

    // Pointer is outside the editor — no collision
    return [];
  };
}

// ── Provider ──

export function PlaylistDndProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const apolloClient = useApolloClient();
  const defaultPLDuration =
    useAuthStore((s) => s.company?.featureData?.defaultPLDuration) || DEFAULT_DURATION;
  // Ref mirrors Zustand value for synchronous access in memoized DnD callbacks
  const defaultPLDurationRef = useRef(defaultPLDuration);
  defaultPLDurationRef.current = defaultPLDuration;
  const { resolveFolderAndConfirm } = useFolderAdd();
  const editorRef = useRef<EditorDndCallbacks | null>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  // Ref mirrors state for synchronous access in callbacks without re-creating them.
  const activeDragRef = useRef<DragItemData | null>(null);
  // Live pointer position for the overlay snap modifier (updated via pointermove listener).
  const pointerRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const [activeDragData, setActiveDragData] = useState<DragItemData | null>(null);
  const [externalDropIndex, setExternalDropIndex] = useState<number | null>(null);
  const [isExternalDragOverEditor, setIsExternalDragOverEditor] = useState(false);
  const [canEditActivePlaylist, setCanEditActivePlaylist] = useState(true);

  // Track live pointer position during drag for the overlay modifier.
  // Also set cursor: copy on body for external drags (overlay has pointer-events: none).
  useEffect(() => {
    if (!activeDragData) return;
    const handler = (e: PointerEvent) => {
      pointerRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('pointermove', handler);
    const isExternal = activeDragData.source !== 'editor';
    let styleEl: HTMLStyleElement | null = null;
    if (isExternal) {
      // Force cursor: copy globally — inline body.style.cursor is overridden by
      // child elements (e.g. cursor-grab on rows), so inject a !important rule.
      styleEl = document.createElement('style');
      styleEl.textContent = '* { cursor: copy !important; }';
      document.head.appendChild(styleEl);
    }
    return () => {
      window.removeEventListener('pointermove', handler);
      if (styleEl) {
        styleEl.remove();
      }
    };
  }, [activeDragData]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Restrict editor-source drags to vertical axis AND within the scroll container.
  // External drags (left/right panels) are clamped within the PlaylistsPage container (pageRef).
  const editorContainModifier = useCallback<Modifier>(
    ({ transform, draggingNodeRect, scrollableAncestorRects }) => {
      const data = activeDragRef.current;
      if (!data) return transform;

      // Editor items: vertical only + clamped to scroll container
      if (data.source === 'editor') {
        let y = transform.y;
        const container = scrollableAncestorRects[0];
        if (draggingNodeRect && container) {
          if (draggingNodeRect.top + y <= container.top) {
            y = container.top - draggingNodeRect.top;
          } else if (draggingNodeRect.bottom + y >= container.top + container.height) {
            y = container.top + container.height - draggingNodeRect.bottom;
          }
        }
        return { ...transform, x: 0, y };
      }

      // External items (left/right panels): clamp within PlaylistsPage container
      const pageRect = pageRef.current?.getBoundingClientRect();
      if (draggingNodeRect && pageRect) {
        let { x, y } = transform;
        if (draggingNodeRect.left + x <= pageRect.left) {
          x = pageRect.left - draggingNodeRect.left;
        } else if (draggingNodeRect.right + x >= pageRect.right) {
          x = pageRect.right - draggingNodeRect.right;
        }
        if (draggingNodeRect.top + y <= pageRect.top) {
          y = pageRect.top - draggingNodeRect.top;
        } else if (draggingNodeRect.bottom + y >= pageRect.bottom) {
          y = pageRect.bottom - draggingNodeRect.bottom;
        }
        return { ...transform, x, y };
      }

      return transform;
    },
    [],
  );
  const modifiers = [editorContainModifier];

  // Overlay modifier: positions the drag pill just below the live cursor
  // by computing the offset from the overlay's anchor (draggingNodeRect) to the
  // current pointer position. Applied to all drag sources.
  const overlaySnapModifier = useCallback<Modifier>(({ transform, draggingNodeRect }) => {
    if (!draggingNodeRect) return transform;
    const { x, y } = pointerRef.current;
    return {
      ...transform,
      x: x - draggingNodeRect.left + 8,
      y: y - draggingNodeRect.top + 16,
    };
  }, []);
  const overlayModifiers = [overlaySnapModifier];

  // Stable collision detection that reads drag source from ref
  const collisionDetection = useCallback<CollisionDetection>(
    (args) => createPlaylistCollisionDetection(activeDragRef)(args),
    [],
  );

  const registerEditor = useCallback((callbacks: EditorDndCallbacks | null) => {
    editorRef.current = callbacks;
    // Update state so consumers (e.g., PlaylistListItem) can disable drag when center editor is read-only
    setCanEditActivePlaylist(callbacks?.canEditPlaylist ?? true);
  }, []);

  // ── Unified add methods (shared by DnD drops and button clicks) ──

  /** Build optimistic PlaylistAsset from asset ID using Apollo cache. */
  const buildOptimisticAsset = useCallback(
    (id: string, fallbackName?: string): PlaylistAsset => {
      const cached = apolloClient.readFragment<Asset>({
        id: `Asset:${id}`,
        fragment: ASSET_CACHE_FRAGMENT,
      });
      const plDuration = defaultPLDurationRef.current;
      const rawDuration =
        cached?.duration ??
        (cached?.num_pages && cached.num_pages > 0 ? cached.num_pages * plDuration : null);
      const { duration, srcDuration } = enforceMinDuration(rawDuration, plDuration);
      return {
        _id: id,
        type: cached?.type || 'asset',
        fileType: cached?.fileType,
        appType: cached?.appType,
        webType: cached?.webType,
        commonType: cached?.commonType,
        subType: cached?.subType,
        serviceType: cached?.serviceType,
        filename: cached?.originalFileName || fallbackName,
        thumbnail: cached?.thumbnail,
        duration,
        srcDuration,
        bucket: cached?.bucket,
        AWSS3ID: cached?.AWSS3ID,
      };
    },
    [apolloClient],
  );

  /** Build optimistic PlaylistAsset from a folder asset edge. */
  const buildOptimisticFolderAsset = useCallback((node: Asset): PlaylistAsset => {
    const plDuration = defaultPLDurationRef.current;
    const rawDuration =
      node.duration ?? (node.num_pages && node.num_pages > 0 ? node.num_pages * plDuration : null);
    const { duration, srcDuration } = enforceMinDuration(rawDuration, plDuration);
    return {
      _id: node._id,
      type: node.fileType || node.type || 'asset',
      filename: node.originalFileName || node._id,
      thumbnail: node.thumbnail,
      duration,
      srcDuration,
    };
  }, []);

  /** Unified: add assets by ID — validates duration limit, builds optimistic items, adds to editor. */
  const addAssetsToEditor = useCallback(
    (assetIds: string[]) => {
      const editor = editorRef.current;
      if (!editor) return;

      // Filter out excluded asset types
      const allowedIds = assetIds.filter((id) => {
        const cached = apolloClient.readFragment<Asset>({
          id: `Asset:${id}`,
          fragment: ASSET_CACHE_FRAGMENT,
        });
        return !cached || !isAssetExcludedFromPicker(cached, ASSET_PICKER_TYPES.PLAYLIST);
      });
      if (allowedIds.length === 0) return;

      const optimisticItems = allowedIds.map((id) => buildOptimisticAsset(id));

      const validation = validateAssetAdd(
        editor.playlist,
        editor.localItems,
        optimisticItems,
        false,
      );

      if (!validation.valid) {
        toast.error(t(validation.errorKey ?? 'editSectionDurationLimit', validation.errorParams));
        return;
      }

      // Single asset: apply adjusted duration if needed (auto-clamp to fit)
      if (validation.adjustedDuration !== undefined && optimisticItems.length === 1) {
        optimisticItems[0].srcDuration = optimisticItems[0].duration;
        optimisticItems[0].duration = validation.adjustedDuration;
      }

      editor.handleAddItems(optimisticItems, editor.localItems.length);
    },
    [apolloClient, buildOptimisticAsset, t],
  );

  /** Unified: add folder assets — validates duration limit, builds optimistic items, adds to editor. */
  const addFolderAssetsToEditor = useCallback(
    (edges: Array<{ node: Asset }>) => {
      const editor = editorRef.current;
      if (!editor) return;

      const optimisticItems = edges.map((e) => buildOptimisticFolderAsset(e.node));

      const validation = validateAssetAdd(
        editor.playlist,
        editor.localItems,
        optimisticItems,
        true,
      );

      if (!validation.valid) {
        toast.error(t(validation.errorKey ?? 'editSectionDurationLimit', validation.errorParams));
        return;
      }

      editor.handleAddItems(optimisticItems, editor.localItems.length);
    },
    [buildOptimisticFolderAsset, t],
  );

  /** Unified: add nested playlists — validates nesting + duration, builds optimistic items, adds to editor. */
  const addPlaylistsToEditor = useCallback(
    async (playlistIds: string[]) => {
      const editor = editorRef.current;
      if (!editor) return;

      // Filter out self-reference
      const filteredIds = playlistIds.filter((id) => id !== editor.activePlaylistId);
      if (filteredIds.length === 0) {
        toast.error(t('Cannot add a playlist to itself'));
        return;
      }

      // Validate nesting (circular reference + depth limit) via backend API
      const nestResult = await editor.validateNested({
        currentPlaylistId: editor.activePlaylistId,
        ids: filteredIds,
      });
      if (!nestResult.status) {
        toast.error(t(nestResult.key ?? 'editSectionOnly2levelLimit', nestResult.value));
        return;
      }

      // Read cached playlists for totalDuration
      const cachedPlaylists = filteredIds.map((id) =>
        apolloClient.readFragment<{ _id: string; name?: string; totalDuration?: number }>({
          id: `Playlist:${id}`,
          fragment: PLAYLIST_CACHE_FRAGMENT,
        }),
      );

      // Validate duration limit
      const nestedDurations = cachedPlaylists.map(
        (cached) => cached?.totalDuration ?? DEFAULT_DURATION,
      );
      const validation = validateMultipleNestedPlaylistAdd(
        editor.playlist,
        editor.localItems,
        nestedDurations,
      );

      if (!validation.valid) {
        toast.error(t(validation.errorKey ?? 'editSectionDurationLimit', validation.errorParams));
        return;
      }

      const optimisticItems: PlaylistAsset[] = filteredIds.map((id, index) => {
        const cached = cachedPlaylists[index];
        return {
          _id: id,
          type: 'playlist' as const,
          filename: cached?.name || id,
          duration: cached?.totalDuration ?? DEFAULT_DURATION,
          thumbnail: PLAYLIST_THUMBNAIL_URL,
        };
      });

      editor.handleAddItems(optimisticItems, editor.localItems.length);
    },
    [apolloClient, t],
  );

  // ── Handlers ──

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const data = event.active.data.current as DragItemData;
    activeDragRef.current = data;
    setActiveDragData(data);
    if (data.source === 'editor' || data.source === 'asset-folder') {
      editorRef.current?.cancelAutoSave();
    }
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const data = activeDragRef.current;
    if (!data || data.source === 'editor') return;

    // Block external drops if user cannot edit the active playlist
    const editor = editorRef.current;
    if (!editor?.canEditPlaylist) {
      setExternalDropIndex(null);
      setIsExternalDragOverEditor(false);
      return;
    }

    if (event.over) {
      const overId = String(event.over.id);
      // Check if the over target is an editor target (drop-zone or sortable item index)
      if (overId === 'editor-drop-zone') {
        setExternalDropIndex(editor.localItems.length);
        setIsExternalDragOverEditor(true);
      } else {
        const idx = Number(overId);
        if (!isNaN(idx)) {
          setExternalDropIndex(idx);
          setIsExternalDragOverEditor(true);
        }
      }
    } else {
      setExternalDropIndex(null);
      setIsExternalDragOverEditor(false);
    }
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      const data = activeDragRef.current;

      // Always clean up first
      activeDragRef.current = null;
      setActiveDragData(null);
      setExternalDropIndex(null);
      setIsExternalDragOverEditor(false);

      if (!data || !over) {
        if (data?.source === 'editor' || data?.source === 'asset-folder') {
          editorRef.current?.resumeAutoSave();
        }
        return;
      }

      const editor = editorRef.current;
      if (!editor) return;

      // Block all drops if user cannot edit the active playlist
      if (!editor.canEditPlaylist) {
        toast.error(t('permission_denied'));
        if (data.source === 'editor' || data.source === 'asset-folder') {
          editor.resumeAutoSave();
        }
        return;
      }

      // ── Internal reorder ──
      if (data.source === 'editor') {
        if (active.id !== over.id) {
          editor.handleReorder(String(active.id), String(over.id));
        } else {
          editor.resumeAutoSave();
        }
        return;
      }

      // ── External drop — determine insertion position ──
      const overId = String(over.id);
      const pos =
        overId === 'editor-drop-zone'
          ? editor.localItems.length
          : isNaN(Number(overId))
            ? editor.localItems.length
            : Number(overId);

      if (data.source === 'asset-picker') {
        // DnD drop at specific position — use shared logic but with custom position
        const allowedIds = data.assetIds.filter((id) => {
          const cached = apolloClient.readFragment<Asset>({
            id: `Asset:${id}`,
            fragment: ASSET_CACHE_FRAGMENT,
          });
          return !cached || !isAssetExcludedFromPicker(cached, ASSET_PICKER_TYPES.PLAYLIST);
        });
        if (allowedIds.length === 0) return;

        const optimisticItems = allowedIds.map((id) => buildOptimisticAsset(id, data.name));

        const validation = validateAssetAdd(
          editor.playlist,
          editor.localItems,
          optimisticItems,
          false,
        );

        if (!validation.valid) {
          toast.error(t(validation.errorKey ?? 'editSectionDurationLimit', validation.errorParams));
          return;
        }

        if (validation.adjustedDuration !== undefined && optimisticItems.length === 1) {
          optimisticItems[0].srcDuration = optimisticItems[0].duration;
          optimisticItems[0].duration = validation.adjustedDuration;
        }

        editor.handleAddItems(optimisticItems, pos);
      } else if (data.source === 'playlist-list') {
        // DnD drop at specific position — inline because pos differs from append
        const filteredIds = data.playlistIds.filter((id) => id !== editor.activePlaylistId);
        if (filteredIds.length === 0) {
          toast.error(t('Cannot add a playlist to itself'));
          return;
        }
        const nestResult = await editor.validateNested({
          currentPlaylistId: editor.activePlaylistId,
          ids: filteredIds,
        });
        if (!nestResult.status) {
          toast.error(t(nestResult.key ?? 'editSectionOnly2levelLimit', nestResult.value));
          return;
        }

        const cachedPlaylists = filteredIds.map((id) =>
          apolloClient.readFragment<{ _id: string; name?: string; totalDuration?: number }>({
            id: `Playlist:${id}`,
            fragment: PLAYLIST_CACHE_FRAGMENT,
          }),
        );

        const nestedDurations = cachedPlaylists.map(
          (cached) => cached?.totalDuration ?? DEFAULT_DURATION,
        );
        const validation = validateMultipleNestedPlaylistAdd(
          editor.playlist,
          editor.localItems,
          nestedDurations,
        );

        if (!validation.valid) {
          toast.error(t(validation.errorKey ?? 'editSectionDurationLimit', validation.errorParams));
          return;
        }

        const optimisticItems: PlaylistAsset[] = filteredIds.map((id, index) => {
          const cached = cachedPlaylists[index];
          return {
            _id: id,
            type: 'playlist' as const,
            filename: cached?.name || data.name,
            duration: cached?.totalDuration ?? DEFAULT_DURATION,
            thumbnail: PLAYLIST_THUMBNAIL_URL,
          };
        });
        editor.handleAddItems(optimisticItems, pos);
      } else if (data.source === 'asset-folder') {
        resolveFolderAndConfirm(data.folderPath, data.name, (edges) => {
          addFolderAssetsToEditor(edges);
        });
      }
    },
    [t, apolloClient, resolveFolderAndConfirm, addFolderAssetsToEditor, buildOptimisticAsset],
  );

  const handleDragCancel = useCallback(() => {
    const data = activeDragRef.current;
    activeDragRef.current = null;
    setActiveDragData(null);
    setExternalDropIndex(null);
    setIsExternalDragOverEditor(false);
    if (data?.source === 'editor' || data?.source === 'asset-folder') {
      editorRef.current?.resumeAutoSave();
    }
  }, []);

  const cancelAutoSave = useCallback(() => {
    editorRef.current?.cancelAutoSave();
  }, []);

  const checkDurationLimit = useCallback(
    (itemsToAdd: PlaylistAsset[]): boolean => {
      const editor = editorRef.current;
      if (!editor) return true;
      const validation = validateAssetAdd(editor.playlist, editor.localItems, itemsToAdd);
      if (!validation.valid && validation.errorKey) {
        toast.error(t(validation.errorKey, validation.errorParams));
        return false;
      }
      return true;
    },
    [t],
  );

  const contextValue: PlaylistDndContextValue = {
    registerEditor,
    externalDropIndex,
    isExternalDragOverEditor,
    activeDragData,
    cancelAutoSave,
    checkDurationLimit,
    canEditActivePlaylist,
    addAssetsToEditor,
    addFolderAssetsToEditor,
    addPlaylistsToEditor,
  };

  return (
    <PlaylistDndCtx.Provider value={contextValue}>
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        modifiers={modifiers}
        autoScroll={false}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div ref={pageRef}>{children}</div>
        <DragOverlay dropAnimation={null} modifiers={overlayModifiers}>
          {activeDragData && <DragOverlayContent data={activeDragData} />}
        </DragOverlay>
      </DndContext>
    </PlaylistDndCtx.Provider>
  );
}

// ── Overlay content per drag source ──

function DragOverlayContent({ data }: { data: DragItemData }) {
  const { t } = useTranslation();

  if (data.source === 'editor') {
    const label =
      data.selectedCount > 1
        ? `${data.selectedCount} ${t('items')}`
        : data.item.filename || t('Untitled');
    return (
      <div className="max-w-56 rounded-md bg-foreground/85 px-3 py-1.5 shadow-sm">
        <span className="block truncate text-xs font-medium text-background">{label}</span>
      </div>
    );
  }

  // External sources (asset-picker, playlist-list, asset-folder): compact pill with green plus
  let externalLabel = '';
  if (data.source === 'asset-picker') {
    externalLabel = data.count > 1 ? `${data.count} ${t('assets')}` : data.name;
  } else if (data.source === 'playlist-list') {
    externalLabel = data.count > 1 ? `${data.count} ${t('playlists')}` : data.name;
  } else if (data.source === 'asset-folder') {
    externalLabel = data.name;
  } else {
    return null;
  }

  return (
    <div className="max-w-56 rounded-md bg-foreground/85 px-3 py-1.5 shadow-sm">
      <span className="block truncate text-xs font-medium text-background">{externalLabel}</span>
    </div>
  );
}
